import { showDialog } from '@bpinternal/ui-kit-next'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useMatchRoute, useRouterState } from '@tanstack/react-router'
import { userPreferencesQueryOptions } from '~/queries'
import { useSetAccountPreferences } from '~/hooks/users'
import { useAnnouncementStore } from '../stores/announcementStore'
import { AnnouncementDialog } from '../components/AnnouncementDialog'
import { AnnouncementId, ANNOUNCEMENTS } from '../types'
import { useAppStore } from '~/stores'

type useAnnouncementsProps = {
  workspaceId: string
  botId?: string
  targetAnnouncementIds?: AnnouncementId[]
}

/**
 * React hook that checks for active product announcements and displays
 * the oldest unseen one in a modal dialog.
 *
 * @param {string} workspaceId - The current workspace ID.
 * @param {string} botId - The current bot ID. Required to trigger announcements.
 * @param {AnnouncementId[]} [targetAnnouncementIds] - Optional array of specific
 *   announcement IDs to restrict which announcements can be shown. If omitted, all
 *   active announcements are considered.
 *
 * @remarks
 * - Only triggers when `botId` is defined.
 * - Skips if the route has already triggered an announcement in the current session.
 * - Active announcements are those whose current date is between
 *   `releaseDate` and `releaseDate + daysActive`.
 * - Announcements released before the user's account creation date are automatically
 *   marked as seen and will not be shown.
 * - Updates the user's `seenAnnouncementIds` preference once an announcement is shown.
 */
export const useAnnouncements = ({ workspaceId, botId, targetAnnouncementIds }: useAnnouncementsProps) => {
  const { data: seenAnnouncements } = useSuspenseQuery(userPreferencesQueryOptions({ path: 'seenAnnouncementIds' }))
  const { data: requiresOnboarding } = useSuspenseQuery(userPreferencesQueryOptions({ path: 'requireOnboarding' }))
  const { mutate: setPreferences } = useSetAccountPreferences()
  const currentUser = useAppStore((state) => state.currentUser)

  const matchRoute = useMatchRoute()
  const currentRouteTemplate = useRouterState({
    select: (state) => state.matches.at(-1),
  })

  const visitedRoutes = useAnnouncementStore((state) => state.visitedRoutes)
  const addVisitedRoute = useAnnouncementStore((state) => state.addVisitedRoute)

  if (requiresOnboarding || !botId || !currentRouteTemplate || visitedRoutes.some((route) => matchRoute({ to: route })))
    return

  const activeAnnouncements = ANNOUNCEMENTS.filter((a) => {
    const expirationDate = new Date(a.releaseDate)
    expirationDate.setDate(expirationDate.getDate() + a.daysActive)

    return Date.now() > new Date(a.releaseDate).getTime() && Date.now() < expirationDate.getTime()
  })

  const getAnnouncementsAfterUserCreation = () => {
    if (!currentUser) {
      return []
    }

    const userCreatedAt = new Date(currentUser.createdAt).getTime()

    return activeAnnouncements.filter((a) => {
      const announcementReleaseTime = new Date(a.releaseDate).getTime()
      return announcementReleaseTime >= userCreatedAt
    })
  }

  const oldestUnseenAnnouncement = getAnnouncementsAfterUserCreation()
    .filter((a) => targetAnnouncementIds?.includes(a.id) ?? a)
    .filter((a) => !seenAnnouncements.includes(a.id))
    .sort((a, b) => new Date(a.releaseDate).getTime() - new Date(b.releaseDate).getTime())
    .at(0)

  if (oldestUnseenAnnouncement) {
    addVisitedRoute(currentRouteTemplate.routeId)
    setPreferences({ path: 'seenAnnouncementIds', value: [...seenAnnouncements, oldestUnseenAnnouncement.id] })
    showDialog(
      ({ close }) => (
        <AnnouncementDialog
          close={close}
          workspaceId={workspaceId}
          botId={botId}
          announcement={oldestUnseenAnnouncement}
        />
      ),
      {
        layout: 'content-only',
        size: 'max-w-lg',
      }
    )
  }
}
